import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { Phone, Mail, Send, Shield, Clock, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service_type: ""
  });

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We will contact you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        service_type: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section 
      id="contact" 
      className="relative py-24 md:py-32 bg-[#F9F8F4]"
      ref={ref}
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-[#8F9E82] font-bold mb-4">
            Let's Connect
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#2D2A26] mb-6">
            Book a Consultation
          </h2>
          <p className="text-lg text-[#5C5852] max-w-2xl mx-auto">
            Take the first step toward compassionate support. Reach out today to schedule 
            your consultation.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(74,93,35,0.08)] border border-[#4A5D23]/10"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#2D2A26]">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="bg-[#F9F8F4] border-[#E8E4D9] focus:border-[#4A5D23] rounded-xl"
                    data-testid="input-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2D2A26]">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="bg-[#F9F8F4] border-[#E8E4D9] focus:border-[#4A5D23] rounded-xl"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#2D2A26]">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="bg-[#F9F8F4] border-[#E8E4D9] focus:border-[#4A5D23] rounded-xl"
                    data-testid="input-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service_type" className="text-[#2D2A26]">Service Interest</Label>
                  <select
                    id="service_type"
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleChange}
                    className="flex h-10 w-full items-center justify-between rounded-xl border border-[#E8E4D9] bg-[#F9F8F4] px-3 py-2 text-sm focus:border-[#4A5D23] focus:outline-none focus:ring-1 focus:ring-[#4A5D23] cursor-pointer"
                    data-testid="select-service"
                  >
                    <option value="">Select a service</option>
                    <option value="consultation">Initial Consultation ($100/60min)</option>
                    <option value="end-of-life-hourly">End of Life Care - Hourly ($75/hr)</option>
                    <option value="end-of-life-package">End of Life Care - 4hr Package ($200)</option>
                    <option value="post-mortem">Post Mortem Care ($400 flat)</option>
                    <option value="general">General Inquiry</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-[#2D2A26]">Your Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please share a bit about your situation and how we can help..."
                  rows={5}
                  className="bg-[#F9F8F4] border-[#E8E4D9] focus:border-[#4A5D23] rounded-xl resize-none"
                  data-testid="input-message"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-[#4A5D23] text-white py-4 rounded-full font-medium flex items-center justify-center gap-2 hover:bg-[#3A4A1C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-contact"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </motion.button>

              {/* Privacy Disclosure */}
              <div className="flex items-start gap-3 p-4 bg-[#F9F8F4] rounded-xl border border-[#E8E4D9]">
                <Shield className="w-5 h-5 text-[#4A5D23] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#5C5852] leading-relaxed">
                  <strong className="text-[#2D2A26]">Privacy Promise:</strong> Your information is kept strictly confidential. 
                  We do not send unsolicited emails or phone calls. Your privacy and trust are sacred to us.
                </p>
              </div>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Direct Contact */}
            <div className="bg-[#E8E4D9] rounded-3xl p-8">
              <h3 className="font-['Playfair_Display'] text-2xl text-[#2D2A26] mb-6">
                Reach Out Directly
              </h3>
              
              <div className="space-y-4">
                <a 
                  href="tel:504-374-8174" 
                  className="flex items-center gap-4 p-4 bg-white rounded-2xl hover:shadow-md transition-shadow group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-[#4A5D23] rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8F9E82]">Call or Text</p>
                    <p className="text-lg font-semibold text-[#2D2A26]">504-374-8174</p>
                  </div>
                </a>

                <a 
                  href="mailto:inquiries@benevolentbenefit.me" 
                  className="flex items-center gap-4 p-4 bg-white rounded-2xl hover:shadow-md transition-shadow group"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-[#8F9E82] rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8F9E82]">Email Us</p>
                    <p className="text-lg font-semibold text-[#2D2A26]">inquiries@benevolentbenefit.me</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Consultation Reminder */}
            <div className="bg-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(74,93,35,0.08)] border border-[#4A5D23]/10">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-[#4A5D23]" />
                <h3 className="font-['Playfair_Display'] text-xl text-[#2D2A26]">
                  Consultation Details
                </h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-[#5C5852]">
                  <CheckCircle className="w-5 h-5 text-[#8F9E82]" />
                  <span>60-minute comprehensive session</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C5852]">
                  <CheckCircle className="w-5 h-5 text-[#8F9E82]" />
                  <span>$100 consultation fee</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C5852]">
                  <CheckCircle className="w-5 h-5 text-[#8F9E82]" />
                  <span>$25 deposit required to book</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C5852]">
                  <CheckCircle className="w-5 h-5 text-[#8F9E82]" />
                  <span>In-person or virtual available</span>
                </li>
              </ul>
            </div>

            {/* Response Time */}
            <div className="text-center p-6 bg-[#4A5D23]/5 rounded-2xl">
              <p className="text-[#4A5D23] font-medium">
                We typically respond within 24 hours
              </p>
              <p className="text-sm text-[#5C5852] mt-1">
                For urgent matters, please call directly
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
